/**
 * WebsiteDetail - Detail page for a website in the logged-in area
 * Shows all collected information from the database
 */

import { useState, useEffect } from 'react';
import {
    ArrowLeft, Globe, Mail, Phone, MapPin, Tag, ExternalLink,
    Facebook, Linkedin, Instagram, Twitter, Youtube, Sparkles,
    Calendar, Eye, ToggleRight, ToggleLeft, Edit2, Camera,
    Loader2
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import Button from '../ui/Button';

const WebsiteDetail = ({ websiteId, onBack, onEdit, onGenerateSnapshot, websites }) => {
    const { t, language } = useLanguage();
    const [website, setWebsite] = useState(null);
    const [loading, setLoading] = useState(true);

    const langCode = language?.substring(0, 2) || 'fr';

    useEffect(() => {
        if (websiteId && websites) {
            const found = websites.find(w => w.id === websiteId);
            setWebsite(found || null);
            setLoading(false);
        }
    }, [websiteId, websites]);

    // Parse social links
    const parseSocialLinks = (socialLinksData) => {
        if (!socialLinksData) return {};
        try {
            return typeof socialLinksData === 'string'
                ? JSON.parse(socialLinksData)
                : socialLinksData;
        } catch {
            return {};
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="w-12 h-12 border-4 border-cabaza-purple border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!website) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-600 mb-4">{t.websites?.notFound || 'Site web non trouvé'}</p>
                <Button onClick={onBack} variant="secondary">
                    {t.common?.back || 'Retour'}
                </Button>
            </div>
        );
    }

    const socialLinks = parseSocialLinks(website.socialLinks);
    const displayTitle = website[`title_${langCode}`] || website.title || '';
    const displayDescription = website[`description_${langCode}`] || '';
    const displayCategory = website[`category_${langCode}`] || '';
    const displayKeywords = website[`keywords_${langCode}`] || '';

    // Check if translation is pending (site processed but current language not available)
    const hasAnyTranslation = ['fr', 'en', 'nl', 'de', 'es', 'it', 'pt', 'el'].some(
        lang => website[`title_${lang}`] || website[`description_${lang}`]
    );
    const isTranslationPending = website.processed && hasAnyTranslation && !displayDescription && !displayTitle;

    const socialIcons = {
        facebook: { icon: Facebook, color: 'bg-[#1877F2]', label: 'Facebook' },
        linkedin: { icon: Linkedin, color: 'bg-[#0A66C2]', label: 'LinkedIn' },
        instagram: { icon: Instagram, color: 'bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737]', label: 'Instagram' },
        twitter: { icon: Twitter, color: 'bg-black', label: 'X (Twitter)' },
        youtube: { icon: Youtube, color: 'bg-[#FF0000]', label: 'YouTube' }
    };

    return (
        <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <button
                    onClick={onBack}
                    className="flex items-center text-gray-600 hover:text-cabaza-purple transition-colors mb-4"
                >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    {t.common?.back || 'Retour'}
                </button>
            </div>

            {/* Main Card */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Hero Section with Screenshot */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                        {/* Screenshot */}
                        {website.snapshotPath ? (
                            <div className="w-full md:w-1/3 flex-shrink-0">
                                <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                                    <img
                                        src={website.snapshotPath}
                                        alt={displayTitle || website.url}
                                        className="w-full h-auto"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="w-full md:w-1/3 flex-shrink-0">
                                <div className="bg-white/10 rounded-lg p-8 flex flex-col items-center justify-center min-h-[150px]">
                                    <Camera className="w-12 h-12 mb-2 opacity-50" />
                                    <span className="text-sm opacity-70">{t.websites?.noSnapshot || 'Pas de capture'}</span>
                                </div>
                            </div>
                        )}

                        {/* Title and URL */}
                        <div className="flex-1">
                            {displayCategory && (
                                <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm mb-3">
                                    {displayCategory}
                                </span>
                            )}
                            <h1 className="text-2xl md:text-3xl font-bold mb-3">
                                {displayTitle || new URL(website.url).hostname}
                            </h1>
                            <a
                                href={website.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                            >
                                <Globe className="w-4 h-4" />
                                {website.url}
                                <ExternalLink className="w-4 h-4" />
                            </a>

                            {/* Status badges */}
                            <div className="flex flex-wrap gap-2 mt-4">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${website.isPublic ? 'bg-green-500/20 text-green-100' : 'bg-gray-500/20 text-gray-200'}`}>
                                    {website.isPublic ? <ToggleRight className="w-3 h-3" /> : <ToggleLeft className="w-3 h-3" />}
                                    {website.isPublic ? (t.websites?.public || 'Public') : (t.websites?.private || 'Privé')}
                                </span>
                                {website.processed && (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-100 rounded-full text-xs">
                                        <Sparkles className="w-3 h-3" />
                                        {t.websites?.aiAnalyzed || 'Analysé par IA'}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Translation pending notice */}
                    {isTranslationPending && (
                        <div className="mb-6 flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <Loader2 className="w-5 h-5 text-amber-600 animate-spin flex-shrink-0" />
                            <div>
                                <p className="font-medium text-amber-800">
                                    {t.websites?.translationInProgress || 'Traduction en cours'}
                                </p>
                                <p className="text-sm text-amber-700">
                                    {t.websites?.translationInProgressDesc || `Le contenu en ${langCode.toUpperCase()} sera bientôt disponible. Rafraîchissez la page dans quelques instants.`}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Description */}
                    {displayDescription && (
                        <div className="mb-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                <Tag className="w-5 h-5 text-gray-500" />
                                {t.websites?.descriptionField || 'Description'}
                            </h2>
                            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                                {displayDescription}
                            </p>
                        </div>
                    )}

                    {/* Keywords */}
                    {displayKeywords && (
                        <div className="mb-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-2">
                                {t.websites?.keywordsField || 'Mots-clés'}
                            </h2>
                            <div className="flex flex-wrap gap-2">
                                {displayKeywords.split(',').map((keyword, idx) => (
                                    <span
                                        key={idx}
                                        className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                                    >
                                        {keyword.trim()}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Contact Information */}
                    {(website.contactEmail || website.contactPhone || website.address) && (
                        <div className="mb-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-3">
                                {t.websites?.contact || 'Contact'}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {website.contactEmail && (
                                    <a
                                        href={`mailto:${website.contactEmail}`}
                                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                            <Mail className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <span className="text-gray-700 break-all">{website.contactEmail}</span>
                                    </a>
                                )}
                                {website.contactPhone && (
                                    <a
                                        href={`tel:${website.contactPhone}`}
                                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                            <Phone className="w-5 h-5 text-green-600" />
                                        </div>
                                        <span className="text-gray-700">{website.contactPhone}</span>
                                    </a>
                                )}
                                {website.address && (
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg md:col-span-2">
                                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <MapPin className="w-5 h-5 text-orange-600" />
                                        </div>
                                        <span className="text-gray-700">{website.address}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Social Links */}
                    {Object.keys(socialLinks).some(k => socialLinks[k]) && (
                        <div className="mb-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-3">
                                {t.websites?.socialLinks || 'Réseaux sociaux'}
                            </h2>
                            <div className="flex flex-wrap gap-3">
                                {Object.entries(socialLinks).map(([key, url]) => {
                                    if (!url) return null;
                                    const social = socialIcons[key];
                                    if (!social) return null;
                                    const Icon = social.icon;
                                    return (
                                        <a
                                            key={key}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`w-12 h-12 ${social.color} rounded-lg flex items-center justify-center text-white hover:opacity-80 transition-opacity`}
                                            title={social.label}
                                        >
                                            <Icon className="w-6 h-6" />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Metadata */}
                    <div className="border-t border-gray-200 pt-4 mt-6">
                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                            {website.viewCount > 0 && (
                                <span className="flex items-center gap-1">
                                    <Eye className="w-4 h-4" />
                                    {website.viewCount} {t.websites?.views || 'vues'}
                                </span>
                            )}
                            {website.createdAt && (
                                <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {t.websites?.addedOn || 'Ajouté le'} {new Date(website.createdAt).toLocaleDateString()}
                                </span>
                            )}
                            {website.processedAt && (
                                <span className="flex items-center gap-1">
                                    <Sparkles className="w-4 h-4" />
                                    {t.websites?.analyzedOn || 'Analysé le'} {new Date(website.processedAt).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-gray-200">
                        <Button
                            onClick={() => onEdit(website)}
                            icon={Edit2}
                        >
                            {t.websites?.edit || 'Modifier'}
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => onGenerateSnapshot(website)}
                            icon={Camera}
                        >
                            {t.websites?.generateSnapshot || 'Nouvelle capture'}
                        </Button>
                        <a
                            href={website.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" />
                            {t.websites?.visitSite || 'Visiter le site'}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WebsiteDetail;
